import { createSelector } from '@reduxjs/toolkit';

import type { AudioAssetModel, SlideBackgroundModel } from 'js/src/client';
import { AssetContainerTypes, SourceEnum } from 'js/src/client';
import type { SearchContextValueType } from 'js/src/data/media';
import {
    checkAssetUUID,
    getAssetUuidFromBackgroundAsset,
    getImageAssetFromBackgroundAsset,
    getVideoAssetFromBackgroundAsset,
    SEARCH_CONTEXT,
} from 'js/src/data/media';
import { getBackgroundVideoDurationSecondsMax } from 'js/src/data/media/media-types';
import StoreORM from 'js/src/data/store-orm';
import type { FileUpload, UploadedAsset } from 'js/src/data/uploads/uploads-types';
import {
    FILE_STATUS,
    MINIMUM_FILE_UPLOAD_DIMS,
    uploadedAssetIsAudioAsset,
    uploadedAssetIsBackgroundAsset,
    uploadedAssetIsIconAsset,
} from 'js/src/data/uploads/uploads-types';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function getUploadSession(state: RootState, uploadSessionId: string | null) {
    if (!uploadSessionId) {
        return null;
    }
    return state.uploads.upload_sessions.get(uploadSessionId);
}

export function getUploadedAssetByUuid(
    state: RootState,
    uuid: string,
): ImmutableMap<SlideBackgroundModel> | ImmutableMap<AudioAssetModel> | null {
    const uploadedAssets = state.uploads.uploaded_assets;
    const uploadedAssetsOrm = new StoreORM<UploadedAsset>(uploadedAssets);
    let assetFound: ImmutableMap<SlideBackgroundModel> | ImmutableMap<AudioAssetModel> | null =
        null;
    uploadedAssetsOrm.forEach((uploadedAsset) => {
        if (uploadedAssetIsBackgroundAsset(uploadedAsset)) {
            const asset = uploadedAsset.get('asset');
            const assetType = asset.get('type');
            if (asset.getIn([assetType, 'uuid']) === uuid) {
                assetFound = asset;
            }
        } else if (uploadedAssetIsAudioAsset(uploadedAsset)) {
            const asset = uploadedAsset.get('asset');
            if (asset.get('uuid') === uuid) {
                assetFound = asset;
            }
        }
    });
    return assetFound;
}

export const getAllSessionUploadedAssetUuids = createSelector(
    (state: RootState) => state.uploads.uploaded_assets,
    (uploadedAssets) => {
        const uploadedAssetsOrm = new StoreORM<UploadedAsset>(uploadedAssets);
        const uuids: string[] = [];
        uploadedAssetsOrm.forEach((uploadedAsset) => {
            if (uploadedAssetIsBackgroundAsset(uploadedAsset)) {
                const asset = uploadedAsset.get('asset');
                const uuid = getAssetUuidFromBackgroundAsset(asset);
                if (!uuid) throw new Error('Asset uuid not found– should be impossible');
                uuids.push(uuid);
            } else if (uploadedAssetIsAudioAsset(uploadedAsset)) {
                const asset = uploadedAsset.get('asset');
                uuids.push(checkAssetUUID(asset.get('uuid')));
            } else if (uploadedAssetIsIconAsset(uploadedAsset)) {
                const asset = uploadedAsset.get('asset');
                uuids.push(checkAssetUUID(asset.get('uuid')));
            }
        });
        return ImmutableFromJS<string[]>(uuids);
    },
);

export const getUploadedFileNames = createSelector(
    (state: RootState) => state.uploads.uploaded_assets,
    (uploadedAssets) => {
        const uploadedAssetsOrm = new StoreORM<UploadedAsset>(uploadedAssets);
        const names: (string | null)[] = [];
        uploadedAssetsOrm.forEach((uploadedAsset) => {
            if (uploadedAssetIsBackgroundAsset(uploadedAsset)) {
                const asset = uploadedAsset.get('asset');
                const title =
                    getVideoAssetFromBackgroundAsset(asset)?.get('title') ||
                    getImageAssetFromBackgroundAsset(asset)?.get('title');
                names.push(title ?? null);
            } else if (uploadedAssetIsAudioAsset(uploadedAsset)) {
                const asset = uploadedAsset.get('asset');
                names.push(asset.get('title'));
            } else if (uploadedAssetIsIconAsset(uploadedAsset)) {
                const asset = uploadedAsset.get('asset');
                names.push(asset.get('title'));
            }
        });
        return ImmutableFromJS(names);
    },
);

const selectFiles = (state: RootState, uploadSessionId: string) => {
    const session = getUploadSession(state, uploadSessionId);
    if (session) {
        return session.get('files');
    }
    return StoreORM.newFromIdPrefix<FileUpload, string>('files').data;
};

export const selectFilesToUpload = createSelector([selectFiles], (selectedFiles) => {
    const orm = new StoreORM<FileUpload, string>(selectedFiles);
    const files = orm.query({
        key: ['user_canceled'],
        value: false,
        orderBy: {
            key: ['file_obj', 'name'],
        },
    });
    return files;
});

export const selectAllFilesInUploadSession = createSelector([selectFiles], (selectedFiles) => {
    const orm = new StoreORM<FileUpload, string>(selectedFiles);
    const files = orm.map((file) => file);
    return files;
});

export const selectAllFileIdsInUploadSession = createSelector([selectFiles], (selectedFiles) => {
    const orm = new StoreORM<FileUpload, string>(selectedFiles);
    return orm.getAllIds();
});

// File uploads are still in the uploading process until they are either in the VALIDATED or FAILED status
export function getAreFilesFinishedUploading(state: RootState, uploadSessionId: string) {
    let areFilesFinishedUploading = true;
    const files = selectFiles(state, uploadSessionId);
    const orm = new StoreORM<FileUpload, string>(files);
    orm.forEach((file) => {
        if ([FILE_STATUS.VALIDATED, FILE_STATUS.FAILED].indexOf(file.get('status')) === -1) {
            areFilesFinishedUploading = false;
        }
    });
    return areFilesFinishedUploading;
}

export function getFilesCanBeEdited(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    const filesCanBeEdited = session.get('files_can_be_edited');
    return filesCanBeEdited;
}

export function selectUploadsFolderId(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    return session?.get('folderId');
}

export function selectUploadsAttribution(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    const attribution = session.get('attribution');
    return attribution;
}

export function selectUploadsMediaTags(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    const mediaTags = session.get('media_tags');
    return mediaTags;
}

export function selectUploadsLicense(state: RootState, uploadSessionId: string) {
    const session = getUploadSession(state, uploadSessionId);
    if (!session) throw new Error('Upload session not found');
    const license = session.get('license');
    return license;
}

export function selectUploadsFailMessages(state: RootState) {
    return state.uploads.fail_messages;
}

export function getActualMediaUploadProgress(state: RootState) {
    const progressMap = state.uploads.progress;
    if (progressMap.size <= 0) {
        return -1;
    }
    let total = 0;
    progressMap.map((progress: number) => {
        total += progress;
    });
    return total / progressMap.size;
}

export function shouldShowDurationWarning(mediaItem: ImmutableMap<FileUpload>) {
    const durationSeconds = mediaItem.get('file_metadata').get('duration');
    if (durationSeconds) {
        const startMillis = mediaItem.get('file_metadata').get('start_millis') || 0;
        const endMillis =
            mediaItem.get('file_metadata').get('end_millis') || durationSeconds * 1000;
        const trimmedDurationSeconds = (endMillis - startMillis) / 1000;
        if (trimmedDurationSeconds > getBackgroundVideoDurationSecondsMax()) {
            return true;
        }
    }
    return false;
}

export const getFileObj = (file: ImmutableMap<FileUpload>) => {
    return file.get('file_obj');
};

export function shouldShowRecommendedMediaSizeWarning(
    files: Immutable.List<ImmutableMap<FileUpload>>,
) {
    let show_warning = false;
    files.forEach((file) => {
        if (!file.get('uploaded_asset')) {
            return;
        }

        const asset_type = file.get('uploaded_asset')?.get('type');
        if (asset_type !== AssetContainerTypes.BACKGROUND) {
            return;
        }

        const background_type = file
            .get('uploaded_asset')
            ?.get(AssetContainerTypes.BACKGROUND)
            ?.get('type');
        const width = file.getIn([
            'uploaded_asset',
            AssetContainerTypes.BACKGROUND,
            background_type,
            'width',
        ]) as number;
        const height = file.getIn([
            'uploaded_asset',
            AssetContainerTypes.BACKGROUND,
            background_type,
            'height',
        ]) as number;
        if (width < MINIMUM_FILE_UPLOAD_DIMS.width || height < MINIMUM_FILE_UPLOAD_DIMS.height) {
            show_warning = true;
        }
    });
    return show_warning;
}

// Get currently uploading files that match a specific asset type
export const getUploadingFilesBySearchContext = createSelector(
    (state: RootState) => state.uploads.upload_sessions,
    (state: RootState, searchContext: SearchContextValueType) => searchContext,
    (uploadSessions, searchContext) => {
        const assetTypes: AssetContainerTypes[] = [];
        switch (searchContext) {
            case SEARCH_CONTEXT.UPLOADED_ELEMENTS:
                assetTypes.push(AssetContainerTypes.ICON, AssetContainerTypes.BACKGROUND);
                break;
            case SEARCH_CONTEXT.UPLOADED_AUDIO:
                assetTypes.push(AssetContainerTypes.AUDIO);
                break;
            case SEARCH_CONTEXT.UPLOADED_VOICEOVERS:
                assetTypes.push(AssetContainerTypes.AUDIO);
                break;
            case SEARCH_CONTEXT.UPLOADED_VISUALS:
                assetTypes.push(AssetContainerTypes.BACKGROUND);
                break;
            default:
                return []; // No specific asset type for this context
        }
        const matchingFiles: Array<{ id: string; fileUpload: ImmutableMap<FileUpload> }> = [];
        uploadSessions.forEach((session) => {
            const files = session.get('files');
            const orm = new StoreORM<FileUpload, string>(files);
            const allIds = orm.getAllIds();
            allIds.forEach((fileId) => {
                const file = orm.getById(fileId);
                const fileAssetType = file.get('upload_opts').get('asset_type');
                const source = file.get('upload_opts').get('metadata')?.get('source');
                const status = file.get('status');
                const userCanceled = file.get('user_canceled');

                // Include files that are actively uploading or processing
                // Once validated/completed, they should appear as regular assets
                if (
                    assetTypes.includes(fileAssetType) &&
                    !userCanceled &&
                    (status === FILE_STATUS.LOADING || status === FILE_STATUS.FINALIZING)
                ) {
                    // Voice uploads should only appear in voice tab, music uploads only in music tab
                    switch (searchContext) {
                        case SEARCH_CONTEXT.UPLOADED_VOICEOVERS: {
                            if (source === SourceEnum.SOURCE_USER_VOICEOVER) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }

                            break;
                        }
                        case SEARCH_CONTEXT.UPLOADED_AUDIO: {
                            if (source === SourceEnum.SOURCE_USER) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }

                            break;
                        }
                        case SEARCH_CONTEXT.UPLOADED_ELEMENTS: {
                            if (
                                source === SourceEnum.SOURCE_USER &&
                                fileAssetType === AssetContainerTypes.ICON
                            ) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }
                            if (source === SourceEnum.SOURCE_USER_STICKERS) {
                                matchingFiles.push({ id: fileId, fileUpload: file });
                            }

                            break;
                        }
                        default: {
                            matchingFiles.push({ id: fileId, fileUpload: file });
                        }
                    }
                }
            });
        });
        return matchingFiles;
    },
);

// Get the session ID for a specific file
export const getSessionIdForFile = (state: RootState, fileId: string): string | null => {
    let foundSessionId: string | null = null;
    state.uploads.upload_sessions.forEach((session, sessionId) => {
        if (foundSessionId) return; // Early exit if already found
        const files = session.get('files');
        const orm = new StoreORM<FileUpload, string>(files);
        const file = orm.getByIdOrUndefined(fileId);
        if (file) {
            foundSessionId = sessionId;
        }
    });
    return foundSessionId;
};

// Get the upload progress for a specific file
// NOTE: returns the progress for the WHOLE session (will refactor at some point to track the progress per file)
export const getFileUploadProgress = (state: RootState, fileId: string): number => {
    const sessionId = getSessionIdForFile(state, fileId);
    if (!sessionId) return -1;

    const progress = state.uploads.progress.get(sessionId);
    return progress !== undefined ? progress : -1;
};

export const uploadSessionExists = (state: RootState) => {
    return state.uploads.upload_sessions.size > 0;
};
